/**
 * T-17 D11 — ONE error presenter.
 *
 * `ApiError.message` IS the raw response body (apiFetch builds it that way), so
 * every page that rendered `err.message` printed the JSON envelope — a 403
 * showed `{"error":"PermissionDenied","message":"Missing permission: …"}` to the
 * user. `describeApiError` turns any thrown value into text a person can read:
 *
 *  - never returns an object where a string belongs (`error:{code,message}`
 *    bodies are unwrapped; see `extractBodyText`), never `[object Object]`;
 *  - never echoes a body it could not parse (HTML 502 pages, truncated JSON);
 *  - keeps the SQL / driver-internals guard of `errors.ts`;
 *  - a 403 names the missing permission (`required` → "Resource.action", else
 *    the "Missing permission: X.y" message text).
 *
 * Unknown stays unknown: a non-error value yields `kind:'unknown'` with the
 * caller's fallback, never a guessed cause.
 */
import { ApiError } from '@/api/v2Client';
import { extractBodyText, looksLikeRawDbError } from '@/lib/errors';

export type ApiErrorKind =
  | 'forbidden'
  | 'unauthenticated'
  | 'not-found'
  | 'validation'
  | 'server'
  | 'network'
  | 'unknown';

export interface ApiErrorView {
  kind: ApiErrorKind;
  title: string;
  message: string;
  permission?: string;
  status?: number;
  /** The server's correlation id (safeErrorHandler), when the body carries one — quote it to find the server log. */
  correlationId?: string;
}

/** The `correlationId` safeErrorHandler puts on an error body, if any. */
function correlationIdFrom(err: ApiError): string | undefined {
  try {
    const parsed: unknown = JSON.parse(err.body);
    if (parsed !== null && typeof parsed === 'object') {
      const id = (parsed as { correlationId?: unknown }).correlationId;
      if (typeof id === 'string' && /^[A-Za-z0-9._:-]{1,100}$/.test(id)) return id;
    }
  } catch {
    /* not JSON — no correlation id */
  }
  return undefined;
}

const GENERIC_FALLBACK = 'Something went wrong. Please try again.';

/** A body message is shown only if it is plain prose (not JSON/markup, not DB internals). */
function safeText(text: string | null): string | null {
  if (text === null) return null;
  const t = text.trim();
  if (t === '') return null;
  if (t.startsWith('{') || t.startsWith('[') || t.startsWith('<')) return null;
  if (t.includes('"error":') || t.includes('[object Object]')) return null;
  if (looksLikeRawDbError(t)) return null;
  return t;
}

const MISSING_PERMISSION_RE = /Missing permission:\s*([A-Za-z][A-Za-z0-9_]*)[.:]([A-Za-z][A-Za-z0-9_]*)/;

function permissionFrom(err: ApiError): string | undefined {
  // 1. `required: { resource, action }` (the backend's structured field).
  try {
    const parsed: unknown = JSON.parse(err.body);
    if (parsed !== null && typeof parsed === 'object') {
      const required = (parsed as { required?: unknown }).required;
      if (required !== null && typeof required === 'object') {
        const { resource, action } = required as { resource?: unknown; action?: unknown };
        if (typeof resource === 'string' && resource !== '' && typeof action === 'string' && action !== '') {
          return `${resource}.${action}`;
        }
      }
    }
  } catch {
    /* fall through to the message text */
  }
  // 2. "Missing permission: X.y" — in the parsed message, else the raw text
  //    (a truncated body still names the permission).
  const { message } = extractBodyText(err);
  const m = MISSING_PERMISSION_RE.exec(message ?? err.body ?? '');
  return m ? `${m[1]}.${m[2]}` : undefined;
}

function isNetworkFailure(err: unknown): boolean {
  if (err instanceof ApiError) return err.status === 0;
  return err instanceof TypeError || (err instanceof Error && /failed to fetch|network ?error|load failed/i.test(err.message));
}

export function describeApiError(err: unknown, fallback: string): ApiErrorView {
  const fb = fallback.trim() !== '' ? fallback : GENERIC_FALLBACK;

  if (err instanceof ApiError && err.status !== 0) {
    const status = err.status;
    // `ApiError.message` equals the raw body when apiFetch built it; only when a
    // caller gave it distinct text is that text a human message worth showing.
    const bodyMessage =
      safeText(extractBodyText(err).message) ??
      (err.message !== err.body ? safeText(err.message) : null);

    if (status === 403) {
      const permission = permissionFrom(err);
      return {
        kind: 'forbidden',
        title: 'Access denied',
        message: permission
          ? `You need the ${permission} permission to do this. If you think this is a mistake, contact an administrator.`
          : "You don't have permission to do this. If you think this is a mistake, contact an administrator.",
        status,
        ...(permission !== undefined && { permission }),
      };
    }
    if (status === 401) {
      return {
        kind: 'unauthenticated',
        title: 'Sign-in required',
        message: bodyMessage ?? 'Your session has expired or you are not signed in. Sign in and try again.',
        status,
      };
    }
    if (status === 404) {
      return { kind: 'not-found', title: 'Not found', message: bodyMessage ?? fb, status };
    }
    if (status === 400 || status === 409 || status === 422) {
      return { kind: 'validation', title: 'Request rejected', message: bodyMessage ?? fb, status };
    }
    if (status >= 500) {
      const correlationId = correlationIdFrom(err);
      return {
        kind: 'server',
        title: 'Server error',
        message: bodyMessage ?? fb,
        status,
        ...(correlationId !== undefined && { correlationId }),
      };
    }
    return { kind: 'unknown', title: 'Something went wrong', message: bodyMessage ?? fb, status };
  }

  if (isNetworkFailure(err)) {
    return {
      kind: 'network',
      title: "Can't reach the server",
      message: 'Check your connection and try again.',
    };
  }

  return { kind: 'unknown', title: 'Something went wrong', message: fb };
}
